import { useTranslator } from '@bluebeam/react-i18n';
import FileUpload from 'components/hoc/FileUpload';
import Upload from 'components/Icons/Upload';
import Label from 'components/inputs/Label';
import { IconButton } from 'components/lib';
import BasicImageModal from 'components/modals/BasicImageModal';
import { InsertFilesOnSubmit } from 'components/modals/InsertFilesModal/InsertFilesModal';
import HasPermissions from 'components/Permissions/HasPermissions';
import { FeatureFlag, useFeatureFlags } from 'featureFlags';
import { List } from 'immutable';
import snakeCase from 'lodash/snakeCase';
import React from 'react';
import { ModalState } from 'records/ModalState';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { supportedImageExtensions } from 'utils/image';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import delete20 from '../../images/icons/delete-20.svg';
import InsertFilesButton from './RFIOverlays/InsertFilesButton';

export interface PunchPhoto {
  name: string;
  url: string;
  id?: string;
  file?: File;
}

interface Props extends WithAppStoreProps {
  id: string;
  punchPhotos: PunchPhoto[];
  projectDriveItems?: List<ProjectDriveSelectedItem>;
  studioItems?: List<StudioProjectFileData>;
  onPhotoUpload: (files: FileList) => void | Promise<void>;
  onPhotoInsert: InsertFilesOnSubmit;
  onPhotoDelete: (photo: PunchPhoto) => void;
}

const OverlayPunchPhotos = (props: Props): React.ReactElement => {
  const { translate } = useTranslator(['mapView', 'buttons']);
  const {
    [FeatureFlag.PunchImportPhotosFromDms]: isPunchImportPhotosFromDmsEnabled,
  } = useFeatureFlags();

  const handlePhotoClick = (name: string, url: string): void => {
    const modalState = new ModalState({
      id: 'basicImage',
      portal: 'main',
      props: { url, name },
      open: true,
      component: BasicImageModal,
    });

    props.actions.createModal(modalState);
  };

  const {
    id,
    punchPhotos,
    projectDriveItems,
    studioItems,
    onPhotoUpload,
    onPhotoInsert,
    onPhotoDelete,
  } = props;

  return (
    <div className="mb3" id={`${id}-container`}>
      <div>
        <Label
          id={`${id}-label`}
          label={`${translate('mapView:overlayPunchPhotos.attachments')} (${
            punchPhotos.length
          })`}
        />
      </div>
      <div className="row oxa max-hs8 pt1">
        {punchPhotos.map((punchPhoto): React.ReactNode => {
          const { name, url } = punchPhoto;
          return (
            <div key={url} className="row cursor-pointer my2">
              <div onClick={(): void => handlePhotoClick(name, url)}>
                <img
                  id={`${id}-img-${snakeCase(name)}`}
                  src={url}
                  alt={url}
                  className="mr3 wrem-7p5 hrem-7p5 of-cover"
                  style={{ borderRadius: '3px' }}
                  key={`${name}-img`}
                />
              </div>
              <IconButton
                aria="Delete"
                onClick={() => onPhotoDelete(punchPhoto)}
                size="small"
                iconSize={20}
                type="button"
                variant="icon-only"
                css={{
                  // overrides default IconButton setting
                  '&:hover:not(:disabled)': {
                    backgroundColor: 'transparent',
                  },
                  position: 'relative',
                  float: 'right',
                  top: '-0.6rem',
                  right: '1.7rem',
                  alignSelf: 'baseline',
                }}
              >
                <img
                  id="create-punch-overlay-delete-photo"
                  src={delete20}
                  alt="delete"
                />
              </IconButton>
            </div>
          );
        })}
      </div>
      <HasPermissions permissions={['addPhoto']}>
        <div className="spacer hrem-p5" />
        <div className="w100 row justify-between">
          {isPunchImportPhotosFromDmsEnabled && (
            <>
              <InsertFilesButton
                id="create-punch-overlay-insert-files-btn"
                initialProjectDriveItems={projectDriveItems}
                initialStudioItems={studioItems}
                onSubmit={onPhotoInsert}
                className="grow-1 ws2"
                drawingsDisabled
                customLabel={translate('buttons:insertPhoto')}
              />
              <div className="ws1" />
            </>
          )}
          <FileUpload
            id={`${id}-upload-input`}
            accept={supportedImageExtensions}
            onFileSelect={onPhotoUpload}
          >
            {({ openFilePicker }): React.ReactNode => (
              <IconButton
                id="punch-upload-photo"
                variant="secondary"
                isLoading={false}
                size="large"
                onClick={openFilePicker}
                className={
                  isPunchImportPhotosFromDmsEnabled
                    ? 'flex-center grow-1 ws2'
                    : ''
                }
              >
                <Upload />
                {translate('mapView:overlayPunchPhotos.uploadPhoto')}
              </IconButton>
            )}
          </FileUpload>
        </div>
      </HasPermissions>
    </div>
  );
};

export default withAppStore<Props>(OverlayPunchPhotos);
